import { Injectable, NgZone, OnDestroy } from '@angular/core';

import { AnnotationRect, PdfAnnotation } from '@core/api/sources/types';

import type { ActiveOverlay, OverlayClickCallback } from '../types';

export type { OverlayClickCallback } from '../types';

/**
 * Renders persistent, zoom-aware highlight overlays on top of pdf.js page canvases.
 *
 * Coordinate system:
 * - LMR backend rects use top-left origin and include native pageWidth/pageHeight.
 * - Legacy/manual rects use bottom-left origin and fall back to pdf.js page dimensions.
 * - CSS canvas dimensions are used for scaling, not device-pixel backing-store size.
 */
@Injectable({ providedIn: 'root' })
export class PdfAnnotationOverlayService implements OnDestroy {
  private _overlays: ActiveOverlay[] = [];
  private _annotations: PdfAnnotation[] = [];
  private _onClickCallback: OverlayClickCallback | null = null;
  private _mutationObserver: MutationObserver | null = null;
  private _resizeObserver: ResizeObserver | null = null;
  private _pdfViewerContainer: Element | null = null;

  constructor(private readonly _zone: NgZone) {}

  /**
   * Scope all DOM queries to a container element instead of the global document.
   * Call this before setAnnotations to avoid cross-viewer conflicts.
   */
  setContainer(container: Element | null): void {
    this._pdfViewerContainer = container;
    this._resizeObserver?.disconnect();
    this._resizeObserver = null;

    if (container && typeof ResizeObserver !== 'undefined') {
      this._resizeObserver = new ResizeObserver(() => this._renderAll());
      this._resizeObserver.observe(container);
    }
  }

  /**
   * Set the full list of annotations to render and re-render all overlays.
   */
  setAnnotations(annotations: PdfAnnotation[], onClickCallback?: OverlayClickCallback): void {
    this._annotations = annotations;
    if (onClickCallback) this._onClickCallback = onClickCallback;
    this._renderAll();
  }

  /**
   * Call after pdf.js has finished rendering a page (pagesLoaded / pageRendered event).
   * Re-renders all overlays to account for zoom changes or newly rendered pages.
   */
  onPagesRendered(): void {
    this._renderAll();
  }

  /**
   * Highlight a single annotation — scrolls to it and adds a pulse CSS class.
   */
  focusAnnotation(
    annotation: PdfAnnotation,
    options?: { skipPageNavigation?: boolean },
  ): void {
    if (this._focusRenderedAnnotation(annotation)) {
      return;
    }

    if (!options?.skipPageNavigation) {
      this._navigateToPage(annotation.pageNumber);
    }
    this._focusWhenPageRendered(annotation);
  }

  clearAll(): void {
    for (const overlay of this._overlays) {
      overlay.element.parentNode?.removeChild(overlay.element);
    }
    this._overlays = [];
  }

  ngOnDestroy(): void {
    this.clearAll();
    this._mutationObserver?.disconnect();
    this._resizeObserver?.disconnect();
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private _renderAll(): void {
    this.clearAll();
    for (const annotation of this._annotations) {
      this._renderAnnotation(annotation);
    }
  }

  private _renderAnnotation(annotation: PdfAnnotation): void {
    const rects = annotation.rects?.length ? annotation.rects : [annotation.rect];
    if (!rects.length) {
      return;
    }

    const root = this._pdfViewerContainer ?? document;
    const pageContainer = root.querySelector(
      `div.page[data-page-number="${annotation.pageNumber}"]`
    ) as HTMLElement | null;

    if (!pageContainer) return;

    const canvas = pageContainer.querySelector('canvas') as HTMLCanvasElement | null;
    if (!canvas) return;

    const canvasRect = canvas.getBoundingClientRect();
    const pageRect = pageContainer.getBoundingClientRect();
    const canvasOffsetLeft = canvasRect.left - pageRect.left - pageContainer.clientLeft;
    const canvasOffsetTop = canvasRect.top - pageRect.top - pageContainer.clientTop;
    const renderedPageWidth = canvasRect.width || canvas.offsetWidth;
    const renderedPageHeight = canvasRect.height || canvas.offsetHeight;

    for (const r of rects) {
      const { pdfPageWidth, pdfPageHeight } = this._getRectPageDimensions(r, annotation.pageNumber);
      const scaleX = renderedPageWidth / pdfPageWidth;
      const scaleY = renderedPageHeight / pdfPageHeight;
      if (!Number.isFinite(scaleX) || !Number.isFinite(scaleY) || scaleX <= 0 || scaleY <= 0) {
        continue;
      }

      const left = canvasOffsetLeft + r.x * scaleX;
      const topInPage = r.coordinateOrigin === 'top-left'
        ? r.y
        : pdfPageHeight - (r.y + r.height);
      const top = canvasOffsetTop + topInPage * scaleY;
      const width = r.width * scaleX;
      const height = r.height * scaleY;

      if (width <= 0 || height <= 0) continue;

      const div = document.createElement('div');
      div.className = 'pdf-annotation-overlay';
      div.dataset.annotationId = annotation._id;
      div.title = annotation.comment || annotation.textContent;
      div.setAttribute('role', 'mark');
      div.setAttribute('aria-label', `Annotation: ${annotation.type} — ${annotation.comment}`);
      div.setAttribute('tabindex', '0');

      const bgColor = this._hexToRgba(annotation.color || '#FFD700', 0.35);
      const borderColor = bgColor;

      div.style.cssText = `
        position: absolute;
        left: ${left}px;
        top: ${top}px;
        width: ${Math.max(width, 4)}px;
        height: ${Math.max(height, 4)}px;
        background-color: ${bgColor};
        border: 1.5px solid ${borderColor};
        border-radius: 2px;
        z-index: 99999;
        pointer-events: auto;
        cursor: pointer;
        box-sizing: border-box;
        transition: background-color 0.15s;
      `;

      div.addEventListener('click', () => {
        this._zone.run(() => this._onClickCallback?.(annotation));
      });
      div.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._zone.run(() => this._onClickCallback?.(annotation));
        }
      });
      div.addEventListener('mouseenter', () => {
        div.style.backgroundColor = this._hexToRgba(annotation.color || '#FFD700', 0.6);
      });
      div.addEventListener('mouseleave', () => {
        div.style.backgroundColor = bgColor;
      });

      // Ensure the page container is relatively positioned
      if (window.getComputedStyle(pageContainer).position === 'static') {
        pageContainer.style.position = 'relative';
      }

      pageContainer.appendChild(div);
      this._overlays.push({ element: div, annotation });
    }
  }

  private _getRectPageDimensions(
    rect: AnnotationRect,
    pageNumber: number
  ): { pdfPageWidth: number; pdfPageHeight: number } {
    if (rect.pageWidth && rect.pageHeight) {
      return { pdfPageWidth: rect.pageWidth, pdfPageHeight: rect.pageHeight };
    }

    return this._getPdfPageDimensions(pageNumber);
  }

  private _focusRenderedAnnotation(annotation: PdfAnnotation): boolean {
    const existing = this._overlays.find((o) => o.annotation._id === annotation._id);
    if (existing) {
      this._scrollToElement(existing.element);
      this._pulse(existing.element);
      return true;
    }

    this._renderAnnotation(annotation);
    const created = this._overlays.find((o) => o.annotation._id === annotation._id);
    if (!created) {
      return false;
    }

    this._scrollToElement(created.element);
    this._pulse(created.element);
    return true;
  }

  private _focusWhenPageRendered(annotation: PdfAnnotation, attempt = 0): void {
    if (attempt >= 10) {
      return;
    }

    window.setTimeout(() => {
      if (this._focusRenderedAnnotation(annotation)) {
        return;
      }

      this._focusWhenPageRendered(annotation, attempt + 1);
    }, attempt < 3 ? 150 : 350);
  }

  private _navigateToPage(pageNumber: number): void {
    try {
      const pdfApp = (window as any).PDFViewerApplication;
      if (pdfApp) {
        if (pdfApp.pdfViewer) {
          pdfApp.pdfViewer.currentPageNumber = pageNumber;
        }
        pdfApp.page = pageNumber;
      }
    } catch {
      // If the viewer API is unavailable, the retry loop still handles already-rendered pages.
    }
  }

  private _getPdfPageDimensions(pageNumber: number): {
    pdfPageWidth: number;
    pdfPageHeight: number;
  } {
    const defaults = { pdfPageWidth: 612, pdfPageHeight: 792 };

    try {
      const pdfApp = (window as any).PDFViewerApplication;
      if (pdfApp?.pdfDocument && pdfApp?.pdfViewer) {
        const pageView = pdfApp.pdfViewer.getPageView(pageNumber - 1);
        if (pageView?.pdfPage) {
          const viewport = pageView.pdfPage.getViewport({ scale: 1 });
          return { pdfPageWidth: viewport.width, pdfPageHeight: viewport.height };
        }
      }
    } catch {
      // fall through to defaults
    }

    return defaults;
  }

  private _scrollToElement(el: HTMLElement): void {
    try {
      const root = this._pdfViewerContainer ?? document;
      const pdfViewer = root.querySelector('ngx-extended-pdf-viewer');
      if (!pdfViewer) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const scrollables = ['#viewer', '.pdfViewer', '.zoom', '[class*="viewer"]'];
      let scrollable: HTMLElement | null = null;
      for (const sel of scrollables) {
        const el = pdfViewer.querySelector(sel) as HTMLElement | null;
        if (el && el.scrollHeight > el.clientHeight) {
          scrollable = el;
          break;
        }
      }

      if (!scrollable) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }

      const elRect = el.getBoundingClientRect();
      const containerRect = scrollable.getBoundingClientRect();
      const targetTop =
        scrollable.scrollTop + (elRect.top - containerRect.top) - scrollable.clientHeight / 2 + elRect.height / 2;

      scrollable.scrollTo({ top: Math.max(0, targetTop), behavior: 'smooth' });
    } catch {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  private _pulse(el: HTMLElement): void {
    el.style.outline = '3px solid white';
    el.style.outlineOffset = '1px';
    el.style.transition = 'outline 0.4s ease';
    setTimeout(() => {
      el.style.outline = '';
      el.style.outlineOffset = '';
    }, 1400);
  }

  private _hexToRgba(hex: string, alpha: number): string {
    const clean = hex.replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16);
    const g = parseInt(clean.substring(2, 4), 16);
    const b = parseInt(clean.substring(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
}
